<template>
  <div>
    <borrow-dialog v-if="showBorrowDialog" @close-modal="showBorrowDialog = false" />
    <dashboard-panel no-collapse :title="panelTitle">
      <snapshot-manager
        :drivers="drivers"
        :get-state-mutators="getStateMutators"
        :snapshot="snapshot"
        storage-key="snapshot-open-loans-list"
      >
        <open-loans-list
          :counterparty.sync="counterparty"
          :detail-loan.sync="detailLoan"
          :detail-loan-initial-tab.sync="detailLoanInitialTab"
          :has-late-returns.sync="hasLateReturns"
          :is-recalled.sync="isRecalled"
          :is-renegotiating.sync="isRenegotiating"
          :omit-headers="omitHeaders"
          :page.sync="page"
          :page-size.sync="pageSize"
          :security.sync="security"
          :show-all.sync="showAll"
          :side.sync="side"
          :sort.sync="sort"
        >
        </open-loans-list>
      </snapshot-manager>
      <loan-details-dialog
        v-if="detailLoan"
        :initial-tab="detailLoanInitialTab"
        :loan-id="detailLoan.id"
        @close-modal="detailLoan = null"
      />
    </dashboard-panel>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import DashboardPanel from '@/modules/dashboard/components/DashboardPanel.vue';
import OpenLoansList from '@/modules/open-loans/components/OpenLoansList.vue';
import SnapshotManager from '@/modules/common/components/SnapshotManager.vue';
import { mapGetters, mapState } from 'vuex';
import i18n from '@/localisation/i18n';
import { CompanyInfo, OpenLoan, Security } from '@/modules/common/models';
import { ClientConfig, UXConfig } from '@/utils/helpers/rest';
import LoanDetailsDialog from '@/modules/open-loans/components/LoanDetailsDialog.vue';
import { SortModelItem } from 'ag-grid-enterprise';

const MAX_ITEMS_PER_PAGE = 1000;

@Component({
  provide() {
    return {
      gridStateKey: 'OpenLoans',
    };
  },
  components: {
    DashboardPanel,
    OpenLoansList,
    SnapshotManager,
    LoanDetailsDialog,
  },
  computed: {
    ...mapState(['clientConfig', 'uxConfig']),
    ...mapGetters(['hasTraderUserRole', 'canBorrow', 'canLend']),
  },
})
export default class OpenLoans extends Vue {
  protected readonly drivers = ['url', 'sessionStorage'];
  protected readonly omitHeaders = [
    'lenderDisplay',
    'borrowerDisplay',
    'displayId',
    'dtccRefId',
    'independentAmount',
    'yesterdayIndependentAmount',
  ];

  protected isRecalled = false;
  protected isRenegotiating = false;
  protected hasLateReturns = false;
  protected counterparty: null | CompanyInfo = null;
  protected security: Security | null = null;
  protected side: 'LOAN' | 'BORROW' | null = null;
  protected sort: SortModelItem = { colId: 'updatedAt', sort: 'desc' };
  protected page = 1;
  protected pageSize = MAX_ITEMS_PER_PAGE;
  protected showAll = false;
  protected detailLoan: OpenLoan | null = null;
  protected detailLoanInitialTab = 'history';

  // store state
  protected hasTraderUserRole!: boolean;
  protected canBorrow!: boolean;
  protected canLend!: boolean;
  protected clientConfig!: ClientConfig;
  protected uxConfig!: UXConfig;

  protected showBorrowDialog = false;

  protected get panelTitle(): string {
    return i18n.t('sidebar.items.openLoans') as string;
  }

  protected get snapshot(): Record<string, string> {
    const snapshot: Record<string, string> = {
      showAll: this.showAll ? 'true' : 'false',
      isRecalled: this.isRecalled ? 'true' : 'false',
      isRenegotiating: this.isRenegotiating ? 'true' : 'false',
      hasLateReturns: this.hasLateReturns ? 'true' : 'false',
      sort: `${this.sort.sort === 'desc' ? '-' : ''}${this.sort.colId}`,
      page: this.page ? this.page.toString() : '1',
      pageSize: this.pageSize ? this.pageSize.toString() : MAX_ITEMS_PER_PAGE.toString(),
    };
    if (this.side) {
      snapshot.side = this.side;
    }
    if (this.counterparty) {
      snapshot.counterpartyDisplayId = this.counterparty.displayId as string;
    }
    if (this.security) {
      snapshot.cusip = this.security.cusip;
    }

    return snapshot;
  }

  protected getStateMutators(): ReturnType<SnapshotManager['getStateMutators']> {
    return {
      isRecalled: (value) => {
        this.isRecalled = value === 'true';
      },
      isRenegotiating: (value) => {
        this.isRenegotiating = value === 'true';
      },
      hasLateReturns: (value) => {
        this.hasLateReturns = value === 'true';
      },
      side: (value) => {
        this.side = value === 'LOAN' || value === 'BORROW' ? value : null;
      },
      showAll: (value) => {
        this.showAll = value === 'true';
      },
      counterpartyDisplayId: async (value) => {
        try {
          const res = await this.$api.userAccounts.fetchCompanyByDisplayId(value);
          if (res) {
            this.counterparty = res;
          } else {
            throw new Error('Failed to fetch selected counterparty');
          }
        } catch (e) {
          this.$log.warn(e);
        }
      },
      cusip: async (value) => {
        try {
          const res = await this.$api.manualLoans.fetchSecurityByCusip(value);
          if (res) {
            this.security = res;
          } else {
            throw new Error('Failed to fetch selected security');
          }
        } catch (e) {
          this.$log.warn(e);
        }
      },
      sort: (value) => {
        this.sort = {
          colId: value.replace(/^-/, ''),
          sort: value.startsWith('-') ? 'desc' : 'asc',
        };
      },
      page: (value) => {
        this.page = parseInt(value, 10);
      },
      pageSize: (value) => {
        this.pageSize = parseInt(value, 10);
      },
    };
  }
}
</script>

<style lang="scss"></style>
